/**
 * CRO PDP helpers: variant-synced save badge + sticky ATC that reuses the real product form.
 * Does not alter app scripts or form fields — only mirrors state and targets form by id.
 */
(function () {
  'use strict';

  /** Auto-select Buy 2 + subscription on PDP load unless the shopper opts out. */
  const PDP_DEFAULT_SELECTION_ENABLED = true;
  /** Show "(3 Jars)" / "(5 Jars)" under Bundler offer labels — off while using % discounts. */
  const SHOW_JAR_SUBTEXT = false;
  const DEV_ANALYTICS_LOG_TAG = '[cro-pdp analytics]';

  /** True on theme dev preview or in the Shopify theme editor. */
  function isDevAnalyticsMode() {
    return (
      /^(127\.0\.0\.1|localhost)$/i.test(window.location.hostname) ||
      (typeof Shopify !== 'undefined' && Shopify.designMode)
    );
  }

  function getProductInfo() {
    return document.querySelector('product-info');
  }

  /**
   * True only on the Volufiline cream PDP.
   * @returns {boolean} Whether this page should keep Claim Offer buyable.
   */
  function isVolufilineOfferPage() {
    const productInfo = getProductInfo();
    return Boolean(productInfo && productInfo.dataset.tavaVolufilineOffer === 'true');
  }

  /**
   * Caffeine PDP with no selling plans should not auto-select a subscription.
   * Other product pages do not have this attribute, so their behavior stays the same.
   * @param {HTMLElement} productInfo Product info root.
   * @returns {boolean} True when subscription defaults must be skipped.
   */
  function caffeinePageSkipsSubscription(productInfo) {
    return Boolean(
      productInfo &&
        productInfo.hasAttribute('data-tava-caffeine-pdp') &&
        productInfo.dataset.tavaCaffeineSellingPlans === 'false'
    );
  }

  /**
   * Reads the monthly selling plan id rendered for the Volufiline cream.
   * @returns {string} Selling plan id, or an empty string.
   */
  function getVolufilineSellingPlanId() {
    const planInput = document.querySelector('[data-tava-volufiline-selling-plan]');
    return planInput && planInput.value ? String(planInput.value) : '';
  }

  /**
   * Puts 2 jars + the monthly subscription onto the live add-to-cart form.
   * @param {HTMLFormElement} form Product add-to-cart form.
   * @param {number} claimQty Number of jars to add.
   * @returns {void}
   */
  function applyVolufilineClaimOffer(form, claimQty) {
    if (!form) return;

    const qtyInput =
      form.querySelector('input[name="quantity"]') ||
      document.querySelector(`input[name="quantity"][form="${form.id}"]`);
    if (qtyInput) qtyInput.value = String(claimQty || 2);

    const planId = getVolufilineSellingPlanId();
    if (!planId) return;

    let planInput = form.querySelector('input[name="selling_plan"]');
    if (!planInput) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }
    planInput.value = planId;
  }

  /** Updates the percent-off badge beside the price when variants change. */
  function initSaveBadge() {
    const productInfo = getProductInfo();
    if (!productInfo || typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

    const sectionId = productInfo.dataset.section;
    const wrap = document.getElementById(`CroSaveBadge-${sectionId}`);
    if (!wrap) return;

    const inner = wrap.querySelector('[data-cro-save-inner]');
    const prefix = wrap.dataset.croSavePrefix || 'Save ';

    function applyVariant(variant) {
      if (!inner) return;
      if (!variant || !variant.compare_at_price || variant.compare_at_price <= variant.price) {
        wrap.setAttribute('hidden', '');
        inner.textContent = '';
        return;
      }
      const pct = Math.round(
        ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
      );
      inner.textContent = `${prefix}${pct}%`;
      wrap.removeAttribute('hidden');
    }

    subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
      applyVariant(evt && evt.data ? evt.data.variant : null);
    });
  }

  /** Toggles Bundler jar-count subtext via CSS class when SHOW_JAR_SUBTEXT is on. */
  function initJarSubtext() {
    if (!SHOW_JAR_SUBTEXT) return;
    const productInfo = getProductInfo();
    if (!productInfo) return;
    productInfo.classList.add('tava-show-jar-subtext');
  }

  /**
   * Randomizes the PDP urgency viewer count between 31 and 42 on each page load.
   * Replaces the leading number in the theme editor viewers_text string.
   */
  function initViewerCount() {
    const productInfo = getProductInfo();
    if (!productInfo) return;

    const line = productInfo.querySelector('.tava-cro-urgency__line--pulse');
    if (!line || line.dataset.tavaViewerReady) return;

    const viewerCount = Math.floor(Math.random() * 12) + 31;

    for (const node of line.childNodes) {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) continue;
      node.textContent = node.textContent.replace(/\d+/, String(viewerCount));
      break;
    }

    line.dataset.tavaViewerReady = 'true';
  }

  /**
   * Selects the Bundler quantity tier on the product form (e.g. Buy 2).
   * @returns {boolean} True when the tier radio was found and activated.
   */
  function selectBundlerQuantityTier(productInfo, quantity) {
    const tierRadio = productInfo.querySelector(
      `input[name="bundle_quantity"][value="${quantity}"]`
    );
    if (!tierRadio) return false;

    tierRadio.checked = true;
    tierRadio.dispatchEvent(new Event('change', { bubbles: true }));
    tierRadio.dispatchEvent(new Event('input', { bubbles: true }));

    const label = tierRadio.closest('label') || productInfo.querySelector(`label[for="${tierRadio.id}"]`);
    label?.click();

    return true;
  }

  /** Checks Auto Refill and selects the Seal subscription radio when present. */
  function ensureSubscriptionSelected(productInfo) {
    if (!productInfo || caffeinePageSkipsSubscription(productInfo)) return;

    const checkbox = productInfo.querySelector('.bndlr-subscription-checkbox');
    if (checkbox && !checkbox.checked) checkbox.click();

    const subs = productInfo.querySelector('input[name^="subs_type_"]:not([value="one_time"])');
    if (!subs || subs.checked) return;
    subs.checked = true;
    subs.dispatchEvent(new Event('change', { bubbles: true }));
    subs.dispatchEvent(new Event('input', { bubbles: true }));
    subs.closest('label')?.click();
  }

  /**
   * Pre-selects Buy 2 + Auto Refill on PDP load.
   * Re-applies after Bundler/Seal re-render until the shopper changes bundle or subscription.
   */
  function initPdpDefaultSelection() {
    if (!PDP_DEFAULT_SELECTION_ENABLED) return;

    const productInfo = getProductInfo();
    if (!productInfo || productInfo.hasAttribute('data-tava-caffeine-pdp')) return;

    const DEFAULT_QTY = 2;
    let userHasInteracted = false;
    let isApplyingDefaults = false;

    /** True when Bundler quantity radios (Buy 1 / Buy 2 / Buy 3) are in the DOM. */
    function bundlerIsReady() {
      return Boolean(productInfo.querySelector('input[name="bundle_quantity"]'));
    }

    /** True when Seal Subscriptions radios are present. */
    function sealIsReady() {
      return Boolean(
        document.getElementById('seal-subs-loaded') ||
          productInfo.querySelector('.sealsubs-container input[name^="subs_type_"]')
      );
    }

    /** Reads whether Buy 2 and subscription (not one-time) are currently selected. */
    function isDefaultSelectionActive() {
      const buy2 = productInfo.querySelector(
        `input[name="bundle_quantity"][value="${DEFAULT_QTY}"]`
      );
      const bundleOk = !buy2 || buy2.checked;
      if (!sealIsReady() || caffeinePageSkipsSubscription(productInfo)) return bundleOk;

      const subs = productInfo.querySelector('input[name^="subs_type_"]:not([value="one_time"])');
      const checkbox = productInfo.querySelector('.bndlr-subscription-checkbox');
      const subsOk = (!subs || subs.checked) && (!checkbox || checkbox.checked);
      return bundleOk && subsOk;
    }

    /** Checks Auto Refill and selects the subscription radio if needed. */
    function applySubscriptionDefault() {
      ensureSubscriptionSelected(productInfo);
    }

    /**
     * Selects Buy 2 + Subscribe when widgets are ready.
     * @returns {boolean} True when defaults are active or the shopper already chose otherwise.
     */
    function applyDefaults() {
      if (userHasInteracted) return true;
      if (isDefaultSelectionActive()) return true;
      if (!bundlerIsReady() && !sealIsReady()) return false;

      isApplyingDefaults = true;
      try {
        if (bundlerIsReady()) {
          const buy2 = productInfo.querySelector(
            `input[name="bundle_quantity"][value="${DEFAULT_QTY}"]`
          );
          if (buy2 && !buy2.checked) selectBundlerQuantityTier(productInfo, DEFAULT_QTY);
        }

        if (sealIsReady() && !caffeinePageSkipsSubscription(productInfo)) {
          applySubscriptionDefault();
          window.setTimeout(applySubscriptionDefault, 120);
        }
      } finally {
        window.setTimeout(function () {
          isApplyingDefaults = false;
        }, 350);
      }

      return isDefaultSelectionActive();
    }

    /** Stops auto-defaulting once the shopper taps a bundle or subscription control. */
    function onUserInteraction(evt) {
      if (isApplyingDefaults) return;
      if (evt && evt.isTrusted === false) return;
      userHasInteracted = true;
      observer.disconnect();
    }

    /** Bundle/subscription targets: Buy 1–3 radios, Auto Refill checkbox, Seal one-time/subscribe radios. */
    function isBundleOrSubscriptionTarget(node) {
      if (!node || !node.closest) return false;
      return Boolean(
        node.closest('input[name="bundle_quantity"]') ||
          node.closest('.bndlr-subscription-checkbox') ||
          node.closest('input[name^="subs_type_"]') ||
          node.closest('.sealsubs-container')
      );
    }

    productInfo.addEventListener(
      'click',
      function (evt) {
        if (isBundleOrSubscriptionTarget(evt.target)) onUserInteraction(evt);
      },
      true
    );

    productInfo.addEventListener(
      'change',
      function (evt) {
        const target = evt.target;
        if (
          target.matches?.('input[name="bundle_quantity"]') ||
          target.matches?.('.bndlr-subscription-checkbox') ||
          target.matches?.('input[name^="subs_type_"]')
        ) {
          onUserInteraction(evt);
        }
      },
      true
    );

    /** Re-apply defaults when Bundler/Seal inject or replace widget markup. */
    let observerTimer = null;
    const observerTarget =
      productInfo.querySelector('product-form') ||
      productInfo.querySelector('form[data-type="add-to-cart-form"]') ||
      productInfo;

    const observer = new MutationObserver(function () {
      if (userHasInteracted) return;
      window.clearTimeout(observerTimer);
      observerTimer = window.setTimeout(function () {
        if (!userHasInteracted) applyDefaults();
      }, 80);
    });

    observer.observe(observerTarget, { childList: true, subtree: true });

    /** Run on load milestones so late mobile widget init still gets defaults. */
    function scheduleAttempts() {
      applyDefaults();
      [500, 1000, 2000, 3500].forEach(function (ms) {
        window.setTimeout(function () {
          if (!userHasInteracted) applyDefaults();
        }, ms);
      });
    }

    window.addEventListener('load', function () {
      if (!userHasInteracted) applyDefaults();
    });

    scheduleAttempts();

    if (isVolufilineOfferPage()) {
      const form = document.querySelector('product-form form[data-type="add-to-cart-form"]');
      applyVolufilineClaimOffer(form, DEFAULT_QTY);
    }
  }

  /**
   * Clears Claim Offer loading state after add-to-cart completes.
   * Cart drawer opens via product-form renderContents — checkout stays on the drawer CTA only.
   */
  function finishClaimOfferAfterAdd(claimBtn, unsubscribeCartUpdate) {
    if (unsubscribeCartUpdate) unsubscribeCartUpdate();
    claimBtn.classList.remove('is-loading');
    claimBtn.removeAttribute('aria-busy');
  }

  /**
   * Selects Buy N + subscription, uses the real Add to cart button, then opens the cart drawer.
   * Matches manual Buy 2 + Subscribe + ATC so Bundler and Seal discounts stay applied.
   */
  function submitClaimOffer(productInfo, form, claimQty, claimBtn) {
    selectBundlerQuantityTier(productInfo, claimQty);
    ensureSubscriptionSelected(productInfo);
    if (isVolufilineOfferPage()) {
      applyVolufilineClaimOffer(form, claimQty);
    }
    window.setTimeout(function () {
      ensureSubscriptionSelected(productInfo);
      if (isVolufilineOfferPage()) {
        applyVolufilineClaimOffer(form, claimQty);
      }
    }, 120);

    claimBtn.classList.add('is-loading');
    claimBtn.setAttribute('aria-busy', 'true');

    let claimOfferFinished = false;
    let unsubscribeCartUpdate = null;

    /** Runs once after Dawn confirms the cart was updated. */
    function onCartAddComplete() {
      if (claimOfferFinished) return;
      claimOfferFinished = true;
      finishClaimOfferAfterAdd(claimBtn, unsubscribeCartUpdate);
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source && evt.source !== 'product-form') return;
        onCartAddComplete();
      });
    }

    window.setTimeout(function () {
      const tierSelected = Boolean(
        productInfo.querySelector(`input[name="bundle_quantity"][value="${claimQty}"]:checked`)
      );

      if (!tierSelected) {
        const qtyInput =
          form.querySelector('input[name="quantity"]') ||
          document.querySelector(`input[name="quantity"][form="${form.id}"]`);
        if (qtyInput) qtyInput.value = String(claimQty);
      }

      if (isVolufilineOfferPage()) {
        applyVolufilineClaimOffer(form, claimQty);
      }

      const mainBtn = productInfo.querySelector('[id^="ProductSubmitButton-"]');
      if (mainBtn && isVolufilineOfferPage() && mainBtn.disabled) {
        mainBtn.disabled = false;
        mainBtn.removeAttribute('disabled');
      }
      if (!mainBtn || mainBtn.disabled) {
        if (unsubscribeCartUpdate) unsubscribeCartUpdate();
        claimBtn.classList.remove('is-loading');
        claimBtn.removeAttribute('aria-busy');
        return;
      }

      mainBtn.click();

      /** Fallback if cartUpdate never fires — still avoid auto-checkout. */
      window.setTimeout(function () {
        if (claimOfferFinished) return;

        fetch(((typeof routes !== 'undefined' && routes.cart_url) || '/cart') + '.js', {
          credentials: 'same-origin',
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            if (!cart.item_count) {
              throw new Error('Cart empty after add');
            }
            onCartAddComplete();
          })
          .catch(function (err) {
            console.error('[tava sticky claim offer]', err);
            finishClaimOfferAfterAdd(claimBtn, unsubscribeCartUpdate);
          });
      }, 3000);
    }, 250);
  }

  /**
   * Sticky "Claim Offer" — mirrors Buy 2 + Subscribe + add to cart, then opens the cart drawer.
   * Uses the live product form so variant + Bundler discounts stay in sync.
   */
  function initStickyAtc() {
    const sticky = document.querySelector('[data-tava-sticky-atc]');
    const claimBtn = sticky?.querySelector('[data-tava-sticky-claim-offer]');
    if (!sticky || !claimBtn) return;

    const claimQty = parseInt(sticky.dataset.tavaClaimQuantity || '2', 10) || 2;
    const productInfo = getProductInfo();
    const form = document.querySelector('product-form form[data-type="add-to-cart-form"]');

    /** Disable claim CTA when the main add-to-cart button is unavailable. */
    function syncClaimAvailability() {
      if (!productInfo) return;
      // Caffeine sticky never falls back to a mask-only add.
      if (productInfo.hasAttribute('data-tava-caffeine-pdp')) {
        claimBtn.disabled = productInfo.dataset.tavaClaimReady !== 'true';
        return;
      }
      if (isVolufilineOfferPage()) {
        claimBtn.disabled = false;
        return;
      }
      const mainBtn = productInfo.querySelector('[id^="ProductSubmitButton-"]');
      if (mainBtn) claimBtn.disabled = mainBtn.disabled;
    }

    syncClaimAvailability();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncClaimAvailability);
    }

    const mainBtn = productInfo && productInfo.querySelector('[id^="ProductSubmitButton-"]');
    if (mainBtn) {
      const obs = new MutationObserver(syncClaimAvailability);
      obs.observe(mainBtn, { attributes: true, attributeFilter: ['disabled', 'class'] });
    }

    claimBtn.addEventListener('click', function () {
      const caffeinePage = productInfo && productInfo.hasAttribute('data-tava-caffeine-pdp');
      if (caffeinePage) {
        if (productInfo.dataset.tavaClaimReady !== 'true' || !window.TavaCaffeinePdp || !window.TavaCaffeinePdp.claimOffer) return;
        if (claimBtn.disabled || claimBtn.classList.contains('is-loading')) return;
        window.TavaCaffeinePdp.claimOffer().catch(function () {});
        return;
      }
      if (claimBtn.disabled || claimBtn.classList.contains('is-loading')) return;
      if (!form || !productInfo) return;

      submitClaimOffer(productInfo, form, claimQty, claimBtn);
    });

    /**
     * Show the bar only after the main ATC has scrolled off the top.
     * Not intersecting while it is still below the fold must stay hidden.
     */
    const sentinel =
      (productInfo && productInfo.dataset.tavaDuoReady === 'true' && productInfo.querySelector('[data-tava-duo-submit]')) ||
      document.querySelector('[id^="ProductSubmitButton-"]') ||
      document.querySelector('.product-form__buttons');
    if (!sentinel) return;

    function updateStickyVisibility() {
      const rect = sentinel.getBoundingClientRect();
      const scrolledPastAtc = rect.bottom <= 0;
      sticky.classList.toggle('is-visible', scrolledPastAtc);
    }

    updateStickyVisibility();

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function () {
        updateStickyVisibility();
      }, { threshold: [0, 0.01, 1] });
      io.observe(sentinel);
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility);
  }

  /**
   * Main gallery prev/next — Dawn scrolls the slider, but desktop thumbnail mode hides
   * inactive slides so only one slide has width and arrow clicks scroll to NaN.
   * Rebind arrows to media-gallery.setActiveMedia instead.
   */
  function initGalleryNavButtons() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaNavBound === 'true') return;
    gallery.dataset.tavaNavBound = 'true';

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    if (!viewer) return;

    let prevBtn = viewer.querySelector('button[name="previous"]');
    let nextBtn = viewer.querySelector('button[name="next"]');
    if (!prevBtn || !nextBtn) return;

    /** Ordered media ids from thumbnails, falling back to main viewer slides. */
    function getMediaIds() {
      const thumbItems = thumbList
        ? Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'))
        : [];
      if (thumbItems.length) {
        return thumbItems.map((item) => item.dataset.target);
      }
      return Array.from(viewer.querySelectorAll('[data-media-id]')).map((slide) => slide.dataset.mediaId);
    }

    /** Index of the active slide in the ordered media list. */
    function getActiveIndex(mediaIds) {
      if (thumbList) {
        const thumbItems = Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'));
        const thumbIdx = thumbItems.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
        if (thumbIdx >= 0) return thumbIdx;
      }
      const activeSlide = viewer.querySelector('[data-media-id].is-active');
      if (!activeSlide) return -1;
      return mediaIds.indexOf(activeSlide.dataset.mediaId);
    }

    /** Disables prev/next at the first and last slide. */
    function updateButtonStates() {
      const mediaIds = getMediaIds();
      const idx = getActiveIndex(mediaIds);
      if (idx < 0 || mediaIds.length < 2) return;

      if (idx <= 0) prevBtn.setAttribute('disabled', '');
      else prevBtn.removeAttribute('disabled');

      if (idx >= mediaIds.length - 1) nextBtn.setAttribute('disabled', '');
      else nextBtn.removeAttribute('disabled');
    }

    /** Switches the main image by one step using the theme media-gallery API. */
    function goToDelta(delta) {
      const mediaIds = getMediaIds();
      if (mediaIds.length < 2 || typeof gallery.setActiveMedia !== 'function') return;

      const idx = getActiveIndex(mediaIds);
      if (idx < 0) return;

      const nextIdx = idx + delta;
      if (nextIdx < 0 || nextIdx >= mediaIds.length) return;

      gallery.setActiveMedia(mediaIds[nextIdx], false);
      updateButtonStates();
    }

    /** Clone buttons so Dawn's broken scroll handlers are removed. */
    function rebindButton(btn, delta) {
      const fresh = btn.cloneNode(true);
      btn.replaceWith(fresh);
      fresh.addEventListener('click', function (evt) {
        evt.preventDefault();
        if (fresh.hasAttribute('disabled')) return;
        goToDelta(delta);
      });
      return fresh;
    }

    prevBtn = rebindButton(prevBtn, -1);
    nextBtn = rebindButton(nextBtn, 1);

    viewer.addEventListener('slideChanged', updateButtonStates);

    if (thumbList) {
      thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
        btn.addEventListener('click', () => window.setTimeout(updateButtonStates, 0));
      });
    }

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => window.setTimeout(updateButtonStates, 0));
    }

    updateButtonStates();
  }

  /** PDP gallery: show 4 upcoming previews; hide the active main image from the strip. */
  function initGalleryPreviewWindow() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaPreviewBound === 'true') return;
    gallery.dataset.tavaPreviewBound = 'true';

    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    if (!thumbList || !viewer) return;

    const items = Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'));
    if (items.length < 2) return;

    /** Returns index of the thumbnail matching the current main slide. */
    function getActiveIndex() {
      return items.findIndex((item) => item.querySelector('.thumbnail[aria-current="true"]'));
    }

    /** Shows only the next 4 carousel images; active slide stays out of the preview row. */
    function updatePreviewWindow() {
      const total = items.length;
      const activeIdx = getActiveIndex();
      if (activeIdx < 0) return;

      const previewCount = Math.min(4, total - 1);
      const visibleIndices = new Set();
      for (let step = 1; step <= previewCount; step += 1) {
        visibleIndices.add((activeIdx + step) % total);
      }

      items.forEach((item, idx) => {
        item.classList.toggle('tava-preview-hidden', !visibleIndices.has(idx));
      });

      thumbList.scrollTo({ left: 0, behavior: 'smooth' });
    }

    viewer.addEventListener('slideChanged', () => {
      requestAnimationFrame(updatePreviewWindow);
    });

    thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
      btn.addEventListener('click', () => {
        setTimeout(updatePreviewWindow, 0);
      });
    });

    const observer = new MutationObserver(() => updatePreviewWindow());
    thumbList.querySelectorAll('button.thumbnail').forEach((btn) => {
      observer.observe(btn, { attributes: true, attributeFilter: ['aria-current'] });
    });

    updatePreviewWindow();
  }

  /**
   * Auto-advances the main product gallery every 7s while the shopper is idle.
   * Resets the timer on taps, swipes, or arrow/thumbnail clicks inside the gallery.
   */
  function initGalleryAutoAdvance() {
    const gallery = document.querySelector('product-info media-gallery');
    if (!gallery || gallery.dataset.tavaAutoAdvanceBound === 'true') return;
    gallery.dataset.tavaAutoAdvanceBound = 'true';

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails-"]');
    if (!viewer) return;

    const thumbItems = thumbList
      ? Array.from(thumbList.querySelectorAll('.thumbnail-list__item[data-target]'))
      : [];
    const slideCount = thumbItems.length || viewer.querySelectorAll('[data-media-id]').length;
    if (slideCount < 2) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const IDLE_MS = 7000;
    let idleTimer = null;

    /** Moves to the next slide, looping back to the first at the end. */
    function advanceSlide() {
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const nativeScrollTo = window.scrollTo.bind(window);

      /** Keeps the page from jumping when Dawn syncs the active gallery slide. */
      function restorePageScroll() {
        nativeScrollTo({ top: scrollY, left: scrollX, behavior: 'instant' });
      }

      let blockWindowScroll = true;
      window.scrollTo = function () {
        if (blockWindowScroll) return;
        return nativeScrollTo.apply(window, arguments);
      };

      try {
        if (thumbItems.length >= 2 && typeof gallery.setActiveMedia === 'function') {
          const activeIdx = thumbItems.findIndex((item) =>
            item.querySelector('.thumbnail[aria-current="true"]')
          );
          const nextIdx = activeIdx < 0 ? 0 : (activeIdx + 1) % thumbItems.length;
          const mediaId = thumbItems[nextIdx].dataset.target;
          if (mediaId) gallery.setActiveMedia(mediaId, false);
        } else if (typeof viewer.setSlidePosition === 'function' && viewer.slider) {
          const nextBtn = viewer.querySelector('button[name="next"]');
          if (nextBtn && !nextBtn.hasAttribute('disabled')) {
            const step = parseInt(nextBtn.dataset.step || '1', 10);
            viewer.setSlidePosition(viewer.slider.scrollLeft + step * viewer.sliderItemOffset);
          } else {
            viewer.setSlidePosition(0);
          }
        }
      } finally {
        restorePageScroll();
        requestAnimationFrame(restorePageScroll);
        window.setTimeout(function () {
          blockWindowScroll = false;
          window.scrollTo = nativeScrollTo;
          restorePageScroll();
        }, 120);
      }
    }

    function clearIdleTimer() {
      if (idleTimer) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
    }

    /** Starts (or restarts) the 7s idle countdown before the next auto-advance. */
    function scheduleAdvance() {
      clearIdleTimer();
      if (reducedMotion.matches || document.hidden) return;

      idleTimer = window.setTimeout(function () {
        advanceSlide();
        scheduleAdvance();
      }, IDLE_MS);
    }

    function onUserActivity() {
      scheduleAdvance();
    }

    ['click', 'touchstart', 'pointerdown'].forEach(function (evt) {
      gallery.addEventListener(evt, onUserActivity, { passive: true });
    });
    viewer.addEventListener('scroll', onUserActivity, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearIdleTimer();
      else scheduleAdvance();
    });

    reducedMotion.addEventListener('change', function () {
      if (reducedMotion.matches) clearIdleTimer();
      else scheduleAdvance();
    });

    scheduleAdvance();
  }

  /** Cleanup handles for testimonial carousel mode switches (mobile ↔ desktop). */
  const testimonialCarouselCleanups = new WeakMap();

  /** Review carousel — desktop scroll; mobile infinite peek carousel with blur. */
  function initTestimonialCarousels() {
    const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('[data-tava-testimonials]').forEach((root) => {
      const track = root.querySelector('[data-tava-testimonials-track]');
      const prev = root.querySelector('[data-tava-testimonials-prev]');
      const next = root.querySelector('[data-tava-testimonials-next]');
      if (!track || !prev || !next) return;

      const existingCleanup = testimonialCarouselCleanups.get(root);
      if (existingCleanup) existingCleanup();

      /**
       * Desktop — native horizontal scroll + arrow nudge (unchanged behavior).
       * @returns {Function} teardown
       */
      function mountDesktopCarousel() {
        root.classList.remove('tava-pdp-testimonials--mobile-carousel');
        track.style.transform = '';
        track.style.transition = '';

        /** Scroll distance for one card step. */
        function pageDelta() {
          const card = track.querySelector('.tava-pdp-testimonials__card');
          const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
          if (card) {
            const w = card.getBoundingClientRect().width;
            if (w > 0) return w + gap;
          }
          const vp = track.parentElement;
          if (!vp || !vp.clientWidth) return 320;
          return Math.max(280, vp.clientWidth * 0.87);
        }

        function slide(dir) {
          track.scrollBy({ left: dir * pageDelta(), behavior: 'smooth' });
        }

        const onPrev = () => slide(-1);
        const onNext = () => slide(1);
        prev.addEventListener('click', onPrev);
        next.addEventListener('click', onNext);

        return function teardownDesktop() {
          prev.removeEventListener('click', onPrev);
          next.removeEventListener('click', onNext);
        };
      }

      /**
       * Mobile — centered active card, peek neighbors, blur, infinite loop.
       * @returns {Function} teardown
       */
      function mountMobileCarousel() {
        root.classList.add('tava-pdp-testimonials--mobile-carousel');
        const viewport = track.parentElement;
        if (!viewport) return function noop() {};

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const PEEK_PX = 28;
        const SWIPE_THRESHOLD = 42;
        const GAP_FALLBACK = 12;
        const AUTO_INTERVAL_MS = 2000;

        const originals = Array.from(
          track.querySelectorAll('.tava-pdp-testimonials__card:not(.tava-pdp-testimonials__card--clone)')
        );
        if (!originals.length) return function noop() {};

        let firstClone = null;
        let lastClone = null;
        let index = 0;
        let animating = false;
        let slideWidth = 0;
        let viewportWidth = 0;
        let gap = GAP_FALLBACK;
        let autoTimer = null;
        let userHolding = false;

        /** Stop the mobile auto-advance timer. */
        function pauseAuto() {
          if (autoTimer) {
            clearInterval(autoTimer);
            autoTimer = null;
          }
        }

        /** Start auto-advance unless paused, reduced motion, or a single slide. */
        function startAuto() {
          pauseAuto();
          if (reducedMotion || originals.length <= 1 || userHolding) return;

          autoTimer = setInterval(function () {
            if (userHolding || animating || document.hidden) return;
            goNext();
          }, AUTO_INTERVAL_MS);
        }

        /** Pause auto-scroll while the shopper holds the carousel. */
        function onHoldStart() {
          userHolding = true;
          pauseAuto();
        }

        /** Resume auto-scroll after the hold ends. */
        function onHoldEnd() {
          if (!userHolding) return;
          userHolding = false;
          startAuto();
        }

        /** Document-level release so hold ends even if pointer leaves the carousel. */
        function onDocumentHoldEnd() {
          onHoldEnd();
        }

        /** Clone first/last slides for seamless infinite loop. */
        if (originals.length > 1) {
          firstClone = originals[0].cloneNode(true);
          lastClone = originals[originals.length - 1].cloneNode(true);
          [firstClone, lastClone].forEach((node) => {
            node.classList.add('tava-pdp-testimonials__card--clone');
            node.setAttribute('aria-hidden', 'true');
            node.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          });
          track.insertBefore(lastClone, originals[0]);
          track.appendChild(firstClone);
          index = 1;
        }

        /** All slide nodes in track order. */
        function slides() {
          return Array.from(track.querySelectorAll('.tava-pdp-testimonials__card'));
        }

        /** Measure viewport, gap, and slide width for transform math. */
        function measure() {
          viewportWidth = viewport.clientWidth;
          gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || GAP_FALLBACK;
          slideWidth = Math.max(240, viewportWidth - PEEK_PX * 2);
          viewport.style.setProperty('--tava-testimonial-slide-width', `${slideWidth}px`);
          viewport.style.setProperty('--tava-testimonial-peek', `${PEEK_PX}px`);
        }

        /** Translate track so slide i is centered with peek gutters. */
        function offsetForIndex(i) {
          const slide = slides()[i];
          if (!slide) return 0;
          const cardWidth = slide.offsetWidth || slideWidth;
          const centerOffset = (viewportWidth - cardWidth) / 2;
          return centerOffset - slide.offsetLeft;
        }

        /** Apply horizontal track transform. */
        function setTransform(x, animate) {
          const duration = reducedMotion ? '0s' : '0.42s';
          const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
          track.style.transition = animate ? `transform ${duration} ${easing}` : 'none';
          track.style.transform = `translate3d(${x}px, 0, 0)`;
        }

        /** Toggle active styling (sharp card vs blurred neighbors). */
        function updateActive() {
          slides().forEach((el, i) => {
            el.classList.toggle('is-active', i === index);
          });
        }

        /** Jump to slide with optional animation; correct clone edges after transition. */
        function goTo(newIndex, animate) {
          if (animating) return;
          index = newIndex;
          updateActive();
          setTransform(offsetForIndex(index), animate);

          if (!animate || reducedMotion) {
            correctLoopPosition(false);
            return;
          }

          animating = true;

          /** After slide animation, silently swap clone ↔ real slide at edges. */
          function onTransitionEnd(event) {
            if (event.target !== track || event.propertyName !== 'transform') return;
            track.removeEventListener('transitionend', onTransitionEnd);
            animating = false;
            correctLoopPosition(false);
          }

          track.addEventListener('transitionend', onTransitionEnd);
        }

        /** Reset index when landed on cloned first/last slide. */
        function correctLoopPosition(animate) {
          if (originals.length <= 1) return;
          const lastRealIndex = originals.length;
          if (index === 0) {
            index = lastRealIndex;
            updateActive();
            setTransform(offsetForIndex(index), animate);
          } else if (index === lastRealIndex + 1) {
            index = 1;
            updateActive();
            setTransform(offsetForIndex(index), animate);
          }
        }

        function goNext() {
          if (originals.length <= 1) return;
          goTo(index + 1, true);
        }

        function goPrev() {
          if (originals.length <= 1) return;
          goTo(index - 1, true);
        }

        measure();
        track.scrollLeft = 0;
        updateActive();

        /** Layout settle — remeasure once fonts/slide widths are final. */
        function settlePosition(animate) {
          measure();
          setTransform(offsetForIndex(index), animate);
        }

        settlePosition(false);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            settlePosition(false);
          });
        });

        const resizeObserver =
          typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(function () {
                settlePosition(false);
              })
            : null;
        if (resizeObserver) resizeObserver.observe(viewport);

        const onPrev = () => goPrev();
        const onNext = () => goNext();
        prev.addEventListener('click', onPrev);
        next.addEventListener('click', onNext);

        let touchStartX = 0;
        let touchStartY = 0;
        let touchTracking = false;

        /** Start tracking horizontal swipe on the carousel track. */
        function onTouchStart(event) {
          if (originals.length <= 1) return;
          onHoldStart();
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
          touchTracking = true;
        }

        /** Prefer vertical page scroll unless swipe is clearly horizontal. */
        function onTouchMove(event) {
          if (!touchTracking) return;
          const dx = event.touches[0].clientX - touchStartX;
          const dy = event.touches[0].clientY - touchStartY;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
            event.preventDefault();
          }
        }

        /** Complete swipe when threshold met. */
        function onTouchEnd(event) {
          if (!touchTracking) return;
          touchTracking = false;
          onHoldEnd();
          const dx = event.changedTouches[0].clientX - touchStartX;
          const dy = event.changedTouches[0].clientY - touchStartY;
          if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) goNext();
          else goPrev();
        }

        track.addEventListener('touchstart', onTouchStart, { passive: true });
        track.addEventListener('touchmove', onTouchMove, { passive: false });
        track.addEventListener('touchend', onTouchEnd, { passive: true });
        track.addEventListener('touchcancel', onHoldEnd, { passive: true });

        viewport.addEventListener('pointerdown', onHoldStart);
        document.addEventListener('pointerup', onDocumentHoldEnd);
        document.addEventListener('pointercancel', onDocumentHoldEnd);

        /** Pause auto-scroll when the tab is hidden or section leaves view. */
        function onVisibilityChange() {
          if (document.hidden) pauseAuto();
          else startAuto();
        }

        document.addEventListener('visibilitychange', onVisibilityChange);

        const intersectionObserver =
          typeof IntersectionObserver !== 'undefined'
            ? new IntersectionObserver(
                function (entries) {
                  if (entries[0]?.isIntersecting) startAuto();
                  else pauseAuto();
                },
                { threshold: 0.2 }
              )
            : null;
        if (intersectionObserver) intersectionObserver.observe(root);

        startAuto();

        /** Keep centered on resize / orientation change. */
        function onResize() {
          settlePosition(false);
        }

        window.addEventListener('resize', onResize);

        return function teardownMobile() {
          pauseAuto();
          prev.removeEventListener('click', onPrev);
          next.removeEventListener('click', onNext);
          track.removeEventListener('touchstart', onTouchStart);
          track.removeEventListener('touchmove', onTouchMove);
          track.removeEventListener('touchend', onTouchEnd);
          track.removeEventListener('touchcancel', onHoldEnd);
          viewport.removeEventListener('pointerdown', onHoldStart);
          document.removeEventListener('pointerup', onDocumentHoldEnd);
          document.removeEventListener('pointercancel', onDocumentHoldEnd);
          document.removeEventListener('visibilitychange', onVisibilityChange);
          if (intersectionObserver) intersectionObserver.disconnect();
          window.removeEventListener('resize', onResize);
          if (resizeObserver) resizeObserver.disconnect();
          if (firstClone) firstClone.remove();
          if (lastClone) lastClone.remove();
          track.style.transform = '';
          track.style.transition = '';
          viewport.style.removeProperty('--tava-testimonial-slide-width');
          viewport.style.removeProperty('--tava-testimonial-peek');
          slides().forEach((el) => el.classList.remove('is-active'));
          root.classList.remove('tava-pdp-testimonials--mobile-carousel');
        };
      }

      /** Pick mobile or desktop carousel implementation. */
      function applyMode() {
        const previousCleanup = testimonialCarouselCleanups.get(root);
        if (previousCleanup) previousCleanup();

        const teardown = MOBILE_MQ.matches ? mountMobileCarousel() : mountDesktopCarousel();
        testimonialCarouselCleanups.set(root, teardown);
      }

      applyMode();
      MOBILE_MQ.addEventListener('change', applyMode);
    });
  }

  /**
   * Ingredient carousel — auto-advance, swipe scroll, prev/next, dot nav.
   * Pauses auto-play after user interaction; respects reduced motion.
   */
  function initIngredientsCarousels() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const PAUSE_AFTER_USER_MS = 12000;

    document.querySelectorAll('[data-tava-ingredients-carousel]').forEach((root) => {
      const track = root.querySelector('[data-tava-ingredients-track]');
      const prevBtn = root.querySelector('[data-tava-ingredients-prev]');
      const nextBtn = root.querySelector('[data-tava-ingredients-next]');
      const dots = Array.from(root.querySelectorAll('[data-tava-ingredients-dot]'));
      if (!track) return;

      const cards = () => Array.from(track.querySelectorAll('.tava-pdp-ingredient'));
      if (!cards().length) return;

      const intervalSec = parseInt(root.getAttribute('data-tava-ingredients-interval'), 10) || 8;
      const intervalMs = Math.max(4000, intervalSec * 1000);
      let timer = null;
      let userPausedUntil = 0;
      let scrollSyncTimer = null;
      let isProgrammaticScroll = false;

      /** Reads active slide index from whichever card is nearest the track start. */
      function activeIndex() {
        const list = cards();
        const anchor = track.scrollLeft;
        let closest = 0;
        let minDist = Infinity;
        list.forEach((card, i) => {
          const dist = Math.abs(card.offsetLeft - anchor);
          if (dist < minDist) {
            minDist = dist;
            closest = i;
          }
        });
        return closest;
      }

      /** Syncs active card + dot states. */
      function setActiveState(index) {
        cards().forEach((card, i) => {
          card.classList.toggle('is-active', i === index);
        });
        dots.forEach((dot, i) => {
          const on = i === index;
          dot.classList.toggle('is-active', on);
          dot.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      /** Scrolls the carousel track to a card without scrollIntoView (avoids horizontal page shift on mobile). */
      function scrollToIndex(index, behavior, fromUser) {
        const list = cards();
        const len = list.length;
        const i = ((index % len) + len) % len;
        const target = list[i];
        if (!target) return;
        if (!fromUser) isProgrammaticScroll = true;
        track.scrollTo({
          left: target.offsetLeft,
          behavior: behavior || 'smooth',
        });
        setActiveState(i);
        if (!fromUser) {
          window.setTimeout(() => {
            isProgrammaticScroll = false;
          }, 450);
        }
      }

      function advance() {
        scrollToIndex(activeIndex() + 1);
      }

      function onUserInteract() {
        userPausedUntil = Date.now() + PAUSE_AFTER_USER_MS;
        scheduleAuto();
      }

      function clearAuto() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      function scheduleAuto() {
        clearAuto();
        if (reducedMotion.matches) return;
        timer = setInterval(() => {
          if (document.hidden) return;
          if (Date.now() < userPausedUntil) return;
          advance();
        }, intervalMs);
      }

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) scheduleAuto();
              else clearAuto();
            });
          },
          { threshold: 0.2 }
        );
        io.observe(root);
      } else {
        scheduleAuto();
      }

      prevBtn?.addEventListener('click', () => {
        scrollToIndex(activeIndex() - 1, 'smooth', true);
        onUserInteract();
      });

      nextBtn?.addEventListener('click', () => {
        scrollToIndex(activeIndex() + 1, 'smooth', true);
        onUserInteract();
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.getAttribute('data-tava-ingredients-dot'), 10);
          if (!Number.isNaN(idx)) {
            scrollToIndex(idx, 'smooth', true);
            onUserInteract();
          }
        });
      });

      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(scrollSyncTimer);
          scrollSyncTimer = setTimeout(() => {
            setActiveState(activeIndex());
            if (!isProgrammaticScroll) onUserInteract();
          }, 80);
        },
        { passive: true }
      );

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollToIndex(activeIndex() + 1, 'smooth', true);
          onUserInteract();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollToIndex(activeIndex() - 1, 'smooth', true);
          onUserInteract();
        }
      });

      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) clearAuto();
        else scheduleAuto();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) clearAuto();
        else scheduleAuto();
      });

      setActiveState(0);
    });
  }

  /** Soft fade/slide-in for PDP sections (theme-agnostic). */
  function initRevealOnScroll() {
    const nodes = document.querySelectorAll('.tava-pdp-reveal');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('is-inview'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-inview');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -24px 0px' }
    );
    nodes.forEach((el) => io.observe(el));
  }

  /**
   * Dev-only: confirms default selection is off and no load-time cart/widget automation.
   * Monitors the first 5s (covers former default-selection retry schedule).
   */
  function initPdpLoadDiagnostics() {
    if (!isDevAnalyticsMode()) return;

    console.info(
      DEV_ANALYTICS_LOG_TAG,
      PDP_DEFAULT_SELECTION_ENABLED
        ? 'initPdpDefaultSelection enabled: Buy 2 + subscription'
        : 'initPdpDefaultSelection is DISABLED (PDP_DEFAULT_SELECTION_ENABLED = false)'
    );

    const LOAD_WINDOW_MS = 5000;
    const pageLoadStarted = Date.now();
    let cartAddFetchCount = 0;
    let programmaticWidgetClicks = 0;

    /** True when the event target is a Bundler or Seal control. */
    function isBundlerSealTarget(node) {
      if (!node || !node.closest) return false;
      return Boolean(
        node.closest('input[name="bundle_quantity"]') ||
          node.closest('.bndlr-subscription-checkbox') ||
          node.closest('input[name^="subs_type_"]') ||
          node.closest('.sealsubs-container')
      );
    }

    document.addEventListener(
      'click',
      function (evt) {
        if (Date.now() - pageLoadStarted > LOAD_WINDOW_MS) return;
        if (evt.isTrusted !== false) return;
        if (!isBundlerSealTarget(evt.target)) return;
        programmaticWidgetClicks += 1;
        console.warn(
          DEV_ANALYTICS_LOG_TAG,
          'programmatic Bundler/Seal click during load window',
          evt.target
        );
      },
      true
    );

    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (
          url &&
          /\/cart\/add(\.js)?(\?|$)/.test(url) &&
          Date.now() - pageLoadStarted <= LOAD_WINDOW_MS
        ) {
          cartAddFetchCount += 1;
          console.warn(DEV_ANALYTICS_LOG_TAG, '/cart/add fetch during load window', url);
        }
        return origFetch.apply(this, args);
      };
    }

    window.setTimeout(function () {
      if (programmaticWidgetClicks === 0) {
        console.info(
          DEV_ANALYTICS_LOG_TAG,
          'No programmatic Bundler/Seal control clicks during load window (' +
            LOAD_WINDOW_MS +
            'ms)'
        );
      }
      if (cartAddFetchCount === 0) {
        console.info(
          DEV_ANALYTICS_LOG_TAG,
          'No /cart/add fetch during load window (' + LOAD_WINDOW_MS + 'ms)'
        );
      }
    }, LOAD_WINDOW_MS);
  }

  /**
   * Mirrors the selected Bundler total onto Add to Cart, plus a per-jar line.
   * Ignores compare-at / original price nodes.
   */
  function initAtcPriceMirror() {
    const productInfo = getProductInfo();
    if (!productInfo || !productInfo.classList.contains('tava-atf')) return;

    const priceEl = productInfo.querySelector('[data-tava-atc-price]');
    const unitEl = productInfo.querySelector('[data-tava-atc-unit]');
    if (!priceEl) return;

    /** Formats cents using Shopify's money formatter when available. */
    function formatCents(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    /**
     * Reads the discounted bundle total in cents, never the struck-through compare price.
     * @param {Element|null} card
     * @returns {number|null}
     */
    function readDiscountedCents(card) {
      if (!card) return null;
      const discounted = card.querySelector('.bndlr-discounted-price');
      if (discounted) {
        const attr = discounted.getAttribute('data-currentprice') || discounted.dataset.currentprice;
        const fromAttr = parseInt(attr, 10);
        if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;
      }

      const source = discounted || card.querySelector('.bndlr-price');
      if (!source) return null;
      const clone = source.cloneNode(true);
      clone.querySelectorAll('.bndlr-original-price, s, del, [class*="compare"], [class*="original"]').forEach(
        function (node) {
          node.remove();
        }
      );
      const match = String(clone.textContent || '').replace(/,/g, '').match(/(\d+\.\d{2}|\d+)/);
      if (!match) return null;
      return Math.round(parseFloat(match[1]) * 100);
    }

    function sync() {
      const selected = productInfo.querySelector('input[name="bundle_quantity"]:checked');
      const qty = selected ? parseInt(selected.value, 10) || 1 : 1;
      const card = selected && selected.closest('.bndlr-radio-container');
      const totalCents = readDiscountedCents(card);
      if (!totalCents) return;

      priceEl.textContent = ' • ' + formatCents(totalCents);
      if (unitEl) {
        unitEl.textContent = formatCents(Math.round(totalCents / qty)) + ' / jar';
      }
    }

    productInfo.addEventListener('change', sync, true);
    productInfo.addEventListener('click', function () {
      window.setTimeout(sync, 50);
    });

    const observer = new MutationObserver(function () {
      window.setTimeout(sync, 30);
    });
    observer.observe(productInfo, { childList: true, subtree: true, characterData: true });
    sync();
  }

  /**
   * Shows monthly delivery copy when a Seal subscription radio is selected.
   */
  function initSubscriptionNotes() {
    const productInfo = getProductInfo();
    if (!productInfo || !productInfo.classList.contains('tava-atf')) return;

    /** Marks the Seal radio parent as a segmented control without moving nodes. */
    function markSealTabs() {
      const radio = productInfo.querySelector('input[name^="subs_type_"]');
      if (!radio) return null;
      const label = radio.closest('label') || radio.parentElement;
      const parent = label && label.parentElement;
      if (!parent || parent.matches('form, product-form')) return label;
      parent.classList.add('tava-atf-purchase-toggle');
      return parent;
    }

    function ensureNotes(anchor) {
      let note = productInfo.querySelector('.tava-atf-subs-note');
      let perks = productInfo.querySelector('.tava-atf-subs-perks');
      if (!note) {
        note = document.createElement('p');
        note.className = 'tava-atf-subs-note';
        note.hidden = true;
        note.textContent = 'Delivered monthly';
        anchor.insertAdjacentElement('afterend', note);
      }
      if (!perks) {
        perks = document.createElement('p');
        perks.className = 'tava-atf-subs-perks';
        perks.hidden = true;
        perks.textContent = 'Save 20% • Pause or skip • Easy cancellation';
        note.insertAdjacentElement('afterend', perks);
      }
      return { note: note, perks: perks };
    }

    function sync() {
      const anchor = markSealTabs();
      if (!anchor) return;
      const nodes = ensureNotes(anchor);
      const subscribed = productInfo.querySelector(
        'input[name^="subs_type_"]:not([value="one_time"]):checked'
      );
      const show = Boolean(subscribed);
      nodes.note.hidden = !show;
      nodes.perks.hidden = !show;
    }

    productInfo.addEventListener('change', sync, true);
    const observer = new MutationObserver(function () {
      window.setTimeout(sync, 40);
    });
    observer.observe(productInfo, { childList: true, subtree: true });
    sync();
  }
  /**
   * Opens the write-review dialog and shows a local "Review received" popup.
   * Does not save or send the form anywhere.
   */
  function initWriteReview() {
    document.querySelectorAll('[data-tava-testimonials]').forEach((root) => {
      if (root.dataset.tavaReviewBound === 'true') return;

      const openBtn = root.querySelector('[data-tava-review-open]');
      const formDialog = root.querySelector('[data-tava-review-form-dialog]');
      const thanksDialog = root.querySelector('[data-tava-review-thanks]');
      const form = root.querySelector('[data-tava-review-form]');
      if (!openBtn || !formDialog || !thanksDialog || !form) return;

      root.dataset.tavaReviewBound = 'true';

      openBtn.addEventListener('click', () => formDialog.showModal());

      root.querySelectorAll('[data-tava-review-close]').forEach((btn) => {
        btn.addEventListener('click', () => formDialog.close());
      });

      // Grey X on the thanks popup returns to the PDP
      const thanksClose = root.querySelector('[data-tava-review-thanks-close]');
      if (thanksClose) {
        thanksClose.addEventListener('click', () => thanksDialog.close());
      }

      // Click the dimmed backdrop to dismiss
      formDialog.addEventListener('click', (event) => {
        if (event.target === formDialog) formDialog.close();
      });
      thanksDialog.addEventListener('click', (event) => {
        if (event.target === thanksDialog) thanksDialog.close();
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        form.reset();
        formDialog.close();
        thanksDialog.showModal();
        window.setTimeout(() => {
          if (thanksDialog.open) thanksDialog.close();
        }, 2600);
      });
    });
  }

  function boot() {
    initPdpLoadDiagnostics();
    initSaveBadge();
    initJarSubtext();
    initViewerCount();
    initStickyAtc();
    if (PDP_DEFAULT_SELECTION_ENABLED) {
      initPdpDefaultSelection();
    }
    initGalleryNavButtons();
    initAtcPriceMirror();
    initSubscriptionNotes();
    initGalleryAutoAdvance();
    initTestimonialCarousels();
    initWriteReview();
    initIngredientsCarousels();
    initRevealOnScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
